/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import Link from '@react/react-spectrum/Link';
import Button from '@react/react-spectrum/Button';
import TranslationUtil from '../../utils/translation';
import Icons from '../../utils/iconMapping';
import { interpretKey } from '../../utils/eventUtil';
import { ACTION, APPINFO_ACTION_ID } from '../../utils/enums';
import './appInformation.scss';
import { InformationDialogAnalytics } from '../../analytics';

const ACTION_HANDLER_TYPES = {
    VIEW_MORE_CLICK: 'view-more-click',
    LTS_CLICK: 'long-term-supported-click',
};

class Versions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            incompatibility: { panelExpanded: false },
        };
    }

    // Get the computed actionMap of sectionTitle-sapcode-codexVersion and applicableActionList
    @computed get actionMap() {
        const { data: { tabData } } = this.props;
        const { applicableActionList, latestVersion, previousVersion } = tabData;

        let latestVersionMap = {};
        let previousVersionMap = {};
        latestVersionMap = latestVersion
            ? this.getSectionMap(latestVersion, applicableActionList) : {};
        previousVersionMap = previousVersion
            ? this.getSectionMap(previousVersion, applicableActionList) : {};
        const computedMap = { ...latestVersionMap, ...previousVersionMap };
        return computedMap;
    }

    getSectionMap(versionSection, applicableActionList) {
        const appActionListMap = {};
        versionSection.apps.map((app) => {
            const { actionList, sapcode, codexVersion } = app;
            const keyName = `${versionSection.title}_${sapcode}_${codexVersion}`;
            const filteredButtonList = this.getAppActionIntersection(
                applicableActionList,
                actionList
            );
            if (filteredButtonList) {
                appActionListMap[keyName] = filteredButtonList;
            }
            return null;
        });
        return appActionListMap;
    }

    /* take intersection of app specific actions and view specific
        actions to find button config */
    getAppActionIntersection(viewActionList, appActionList) {
        const actions = {
            buttonList: [],
            labelList: [],
        };

        viewActionList.map((viewSpecificAction) => {
            let action = appActionList.filter(
                appSpecificAction => appSpecificAction.actionId === viewSpecificAction.actionId
            );
            if (action.length > 0) {
                const {
                    actionId,
                    iconType,
                    ariaLabel,
                    localizedText,
                } = action[0];
                action = {
                    actionId,
                    iconType,
                    ariaLabel,
                    localizedText,
                    actionType: viewSpecificAction.actionType,
                };
                switch (action.actionType.component.toLowerCase()) {
                case ACTION.COMPONENT.BUTTON:
                    actions.buttonList.push(action);
                    break;
                case ACTION.COMPONENT.LABEL:
                    actions.labelList.push(action);
                    break;
                default:
                }
            }
            return null;
        });
        return actions;
    }

    togglePanelState() {
        this.setState(prevState => ({
            incompatibility: {
                panelExpanded: !prevState.incompatibility.panelExpanded,
            },
        }));
    }

    renderVersionSection(sectionDetails, appActionMap) {
        const { actionHandler, data: { tabData: { ltsLinkInfo } } } = this.props;
        const ltsText = ltsLinkInfo ? ltsLinkInfo.text : '';
        const { title, apps } = sectionDetails;
        return (
            <div>
                <div className="title-container">
                    <div className="section-title">
                        {title}
                    </div>
                </div>

                { apps.map((app) => {
                    const {
                        appFamilyName, sapcode, codexVersion, tileVersion,
                        onAppAction, isSupportedForLongTerm,
                    } = app;

                    const { incompatibility } = this.state;
                    const { panelExpanded } = incompatibility;
                    const { ChevronRight, ChevronDown } = Icons;
                    const incompatibilityReason = app.incompatibilityReason && app.incompatibilityReason.split('<br>').filter(
                        reason => (reason.trim() !== '')
                    ).join(', ');
                    const productVersionRow = (
                        <div className="product-container" key={codexVersion}>
                            <div className="product-details">
                                <div className="product-details-info">
                                    <div className="product-name-version">
                                        {appFamilyName} ({tileVersion})
                                    </div>
                                    {
                                        isSupportedForLongTerm
                                        && (
                                            <Link
                                                href="#"
                                                className="product-long-term-support"
                                                variant="quiet"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    InformationDialogAnalytics
                                                        .versionLongTermSupportedClicked(
                                                            app,
                                                            ltsLinkInfo
                                                        );
                                                    actionHandler(
                                                        e,
                                                        ACTION_HANDLER_TYPES.LTS_CLICK,
                                                        ltsLinkInfo
                                                    );
                                                }}>
                                                {ltsText}
                                            </Link>
                                        )
                                    }
                                </div>

                                <div className="product-cta">
                                    <div className="product-cta-list">
                                        {
                                            appActionMap[`${title}_${sapcode}_${codexVersion}`]
                                                .buttonList.map((button) => {
                                                    const {
                                                        actionId, actionType,
                                                        localizedText, ariaLabel,
                                                    } = button;
                                                    const isQuiet = (actionType.quiet === true);
                                                    return (
                                                        <Button
                                                            onClick={() => {
                                                                onAppAction(actionId, app);
                                                                InformationDialogAnalytics
                                                                    .versionActionButtonClick(
                                                                        actionId,
                                                                        app
                                                                    );
                                                            }
                                                            }
                                                            onKeyDown={interpretKey({
                                                                onEnter: [null, true],
                                                            })}
                                                            quiet={isQuiet}
                                                            variant={actionType.variant}
                                                            label={localizedText}
                                                            aria-label={ariaLabel}
                                                            key={localizedText} />
                                                    );
                                                })}
                                        {
                                            appActionMap[`${title}_${sapcode}_${codexVersion}`]
                                                .labelList.map((label) => {
                                                    const {
                                                        actionType,
                                                        localizedText,
                                                    } = label;
                                                    const { iconType } = actionType;
                                                    const { Alert } = Icons;
                                                    const errorIcon = (iconType ? <Alert variant={iconType} size="S" /> : null);
                                                    return (
                                                        <span className="label-span">
                                                            {errorIcon}
                                                            <span className="label-span-text">
                                                                {localizedText}
                                                            </span>
                                                        </span>
                                                    );
                                                })
                                        }
                                        {
                                            incompatibilityReason && panelExpanded
                                            && (
                                                <span className="customChevron" onClick={this.togglePanelState.bind(this)}>
                                                    <ChevronDown size="S" />
                                                </span>
                                            )
                                        }
                                        {
                                            incompatibilityReason && !panelExpanded
                                            && (
                                                <span
                                                    className="customChevron"
                                                    onClick={() => {
                                                        this.togglePanelState();
                                                        InformationDialogAnalytics
                                                            .versionActionButtonClick(
                                                                APPINFO_ACTION_ID.NOT_COMPATIBLE,
                                                                app,
                                                                incompatibilityReason
                                                            );
                                                    }}>
                                                    <ChevronRight size="S" />
                                                </span>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>

                            {incompatibilityReason
                                && (
                                    <div className={`product-incompatible-details ${panelExpanded ? 'pannel-expanded' : 'panel-collapsed'}`}>

                                        <span className="details-regular">
                                            {TranslationUtil.getMessage('ccx.incompatible.message-prefix')}
                                        </span>

                                        <span className="details-bold"> {appFamilyName}</span>

                                        <span className="details-regular">
                                            {TranslationUtil.getMessage('ccx.incompatible.message-suffix')}
                                        </span>

                                        <div className="details-bold system-details">
                                            {incompatibilityReason}
                                        </div>
                                    </div>
                                )
                            }

                        </div>
                    );
                    return productVersionRow;
                })}

            </div>
        );
    }

    renderVersionFooter(footerSubText) {
        const { text, viewMore } = footerSubText;
        const { LinkOut } = Icons;
        const { actionHandler } = this.props;
        const { data } = this.props;
        let app;
        if (data.data) {
            ({ app } = data.data);
        }
        return (
            <div className="version-footer">
                <span>{text}</span>

                <Link
                    href="#"
                    className="sub-text-view-more"
                    onClick={(e) => {
                        e.preventDefault();
                        InformationDialogAnalytics
                            .versionViewMoreClicked(
                                app,
                                viewMore
                            );
                        actionHandler(
                            e,
                            ACTION_HANDLER_TYPES.VIEW_MORE_CLICK,
                            viewMore
                        );
                    }}>
                    <span aria-label={viewMore.text}>{viewMore.text}</span>
                    <LinkOut size="XS" />
                </Link>
            </div>
        );
    }


    render() {
        const { data: { tabData } } = this.props;
        const {
            latestVersion, previousVersion, footerSubText,
        } = tabData;

        // Get the computed map of sectionTitle-sapcode-codexVersion and applicableActionList
        const appActionMap = this.actionMap;

        return (
            <div className="tab-content">
                <div className="versions-tab-content">
                    <div className="version-body">
                        {latestVersion
                            && this.renderVersionSection(latestVersion, appActionMap)}
                        {previousVersion
                            && this.renderVersionSection(previousVersion, appActionMap)}
                    </div>
                    {footerSubText && this.renderVersionFooter(footerSubText)}
                </div>
            </div>
        );
    }
}
export default observer(Versions);
